/** @jsxImportSource @emotion/react */
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { css } from "@emotion/react";
import { useLayer, Placement } from "react-laag";
// components
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "..";

type Props = {
  renderElement: (
    toggleOpen: () => void,
    isOpen: boolean,
    setIsOpen: Dispatch<SetStateAction<boolean>>
  ) => JSX.Element;
  renderMenu: (
    toggleOpen: () => void,
    isOpen: boolean,
    setIsOpen: Dispatch<SetStateAction<boolean>>
  ) => JSX.Element;
  onClose?: () => void;
  placement?: Placement;
  triggerOffset?: number;
  style?: React.CSSProperties;
};

const translationOffset = 20;
const translationInitialValuesKeys = {
  top: { y: translationOffset },
  right: { x: -translationOffset },
  bottom: { y: -translationOffset },
  left: { x: translationOffset },
} as const;

export const ContextMenu: React.FC<Props> = ({
  renderElement,
  renderMenu,
  onClose,
  placement = "bottom-start",
  triggerOffset,
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  }, [setIsOpen, onClose]);

  const toggleIsOpen = useCallback(() => {
    setIsOpen((isOpen) => !isOpen);
  }, [setIsOpen]);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen,
    onOutsideClick: handleClose,
    onDisappear: handleClose,
    placement,
    triggerOffset,
    auto: true,
  });

  return (
    <div
      css={css({
        ...style,
      })}
    >
      <div {...triggerProps}>
        {renderElement(toggleIsOpen, isOpen, setIsOpen)}
      </div>
      {renderLayer(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{
                opacity: 0,
                // prettier-ignore
                ...translationInitialValuesKeys[placement.split('-')[0] as keyof typeof translationInitialValuesKeys],
              }}
              animate={{
                opacity: 1,
                x: 0,
                y: 0,
                transition: { duration: 0.12 },
              }}
              exit={{ opacity: 0, transition: { duration: 0.06 } }}
              {...layerProps}
              css={css`
                z-index: 9;
              `}
            >
              <Card
                css={css`
                  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
                `}
              >
                {renderMenu(toggleIsOpen, isOpen, setIsOpen)}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};
